import React from 'react';

import classNames from 'classnames';
import styled from 'styled-components';

import { SWOT_CATEGORIES, WIDGET_FILTER_OPTIONS, WIDGET_FILTER_TYPES, WIDGET_FILTERS } from 'common/Widgets/constants';
import SelectAdvancedFilter from 'components/lib/SelectAdvancedFilter';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { useAppSelector } from 'store/hook';
import { SelectAdvancedFilterOptionType } from 'types/common';
import { RecommendationCommunityFilterType } from 'types/recommendations';

const Container = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 20px;
`;

const ResetContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-shrink: 0;
  gap: 10px;
  padding: 0px 10px;
  margin: 10px 5px;
  border-left: 1px solid var(--line-gray);
`;

const Link = styled.span`
  color: var(--text-secondary);
  font-size: 10px;
  cursor: pointer;
`;

interface RecommendationCommunityFiltersProps {
  widget: number;
  filters: RecommendationCommunityFilterType;
  careTypeOptions: SelectAdvancedFilterOptionType[];
  unitTypeOptions: SelectAdvancedFilterOptionType[];
  onFiltersChange: (filters: Record<string, any>) => void;
}

const RecommendationCommunityFilters = ({
  filters,
  widget,
  careTypeOptions,
  unitTypeOptions,
  onFiltersChange,
}: RecommendationCommunityFiltersProps) => {
  const hasCareTypeOptions = careTypeOptions.length > 0;
  const hasUnitTypeOptions = unitTypeOptions.length > 0;

  const { competitors } = useCommunityViewContext();

  const reviewCategoriesOptions = useAppSelector((state) =>
    state.global.reviewCategories?.map((item) => ({
      label: item.label,
      value: item.id,
    }))
  );

  const competitorsFilterOptions = competitors?.map((item) => ({
    label: item.comunity_name,
    value: item.id,
    color: item.color,
  }));

  const handleSelection = (filedName: string) => (selection: any) => {
    onFiltersChange({ [filedName]: selection.map((c: any) => c.value) });
  };

  const resetFilters = () => {
    onFiltersChange({
      careTypes: [],
      unitTypes: [],
      careLevels: [],
      reviewSources: [],
      reviewCategories: [],
      competitors: [],
    });
  };

  return (
    <Container>
      <SelectAdvancedFilter
        placeholder="Competitors"
        options={competitorsFilterOptions}
        defaultValue={filters.competitors}
        onSelect={handleSelection('competitors')}
        className={classNames({ active: filters.competitors?.length > 0 })}
      />
      {WIDGET_FILTERS[widget]?.includes(WIDGET_FILTER_TYPES.CARE_TYPE) && hasCareTypeOptions && (
        <SelectAdvancedFilter
          placeholder="Care Type"
          options={careTypeOptions}
          defaultValue={filters.careTypes}
          onSelect={handleSelection('careTypes')}
          className={classNames({ active: filters.careTypes.length > 0 })}
        />
      )}
      {WIDGET_FILTERS[widget]?.includes(WIDGET_FILTER_TYPES.ROOM_TYPE) && hasUnitTypeOptions && (
        <SelectAdvancedFilter
          placeholder="Room Type"
          options={unitTypeOptions}
          defaultValue={filters.unitTypes}
          onSelect={handleSelection('unitTypes')}
          className={classNames({ active: filters.unitTypes.length > 0 })}
        />
      )}
      {WIDGET_FILTERS[widget]?.includes(WIDGET_FILTER_TYPES.CARE_LEVEL) && (
        <SelectAdvancedFilter
          placeholder="Care Levels"
          options={WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.CARE_LEVEL].map((item: any) => ({
            label: item.name,
            value: item.id,
          }))}
          defaultValue={filters.careLevels}
          onSelect={handleSelection('careLevels')}
          className={classNames({ active: filters.careLevels.length > 0 })}
        />
      )}
      {WIDGET_FILTERS[widget]?.includes(WIDGET_FILTER_TYPES.REVIEW_SOURCE) && (
        <SelectAdvancedFilter
          placeholder="Review Source"
          options={WIDGET_FILTER_OPTIONS[WIDGET_FILTER_TYPES.REVIEW_SOURCE].map((item: any) => ({
            label: item.name,
            value: item.id,
          }))}
          defaultValue={filters.reviewSources}
          onSelect={handleSelection('reviewSources')}
          className={classNames({ active: filters.reviewSources.length > 0 })}
        />
      )}
      {WIDGET_FILTERS[widget]?.includes(WIDGET_FILTER_TYPES.REVIEW_CATEGORY) && (
        <SelectAdvancedFilter
          placeholder="Review Category"
          options={reviewCategoriesOptions}
          defaultValue={filters.reviewCategories}
          onSelect={handleSelection('reviewCategories')}
          className={classNames({ active: filters.reviewCategories.length > 0 })}
        />
      )}
      {WIDGET_FILTERS[widget]?.includes(WIDGET_FILTER_TYPES.SWOT_CATEGORY) && (
        <SelectAdvancedFilter
          placeholder="SWOT Category"
          options={SWOT_CATEGORIES}
          defaultValue={filters.swotCategories}
          onSelect={handleSelection('swotCategories')}
          className={classNames({ active: filters.swotCategories.length > 0 })}
        />
      )}

      <ResetContainer>
        <Link onClick={resetFilters}>Reset</Link>
      </ResetContainer>
    </Container>
  );
};

export default RecommendationCommunityFilters;
